/* eslint-disable react/jsx-props-no-spreading */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    IventisCellRendererParams,
    TimeCellEditorRef,
    TimeCellEditor,
    IventisCellEditorParams,
    ListItemCellEditorComponent,
    ListItemCellEditorProps,
    IventisFilterRef,
    ListItemFilterComponent,
    ListItemFilterComponentProps,
    ListItemCellComponentProps,
    TimeCellValue,
    IventisCellEditor,
    TextCellEditor,
    IventisGridApi,
    IventisTimeCellData,
} from "@iventis/data-table";
import { isValueValid } from "@iventis/data-table/lib/internal-columns";
import { TextCellContents } from "@iventis/data-table/components/text-cell-renderer";
import React, { forwardRef, FunctionComponent, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { add, addMinutes, addSeconds, differenceInSeconds, format, intervalToDuration } from "date-fns";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { Popover } from "@mui/material";
import { CellWithDropDown } from "@iventis/data-table/components/list-item-cell";
import { addTimeSpanToDate, minutesToTimeSpan, formatTimeSpan } from "@iventis/utilities";
import { utcToZonedTime } from "date-fns-tz";
import { TimeSpan, Preposition } from "@iventis/types";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { customMediaQueryMax, customMediaQueryMin, muiInputFormsCSS, screenSizeBreakpoints, styled } from "@iventis/styles";
import { formFieldVerticalGap, formPadding } from "@iventis/styles/src/atomic-rules";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { RunSheetData, RunSheetRowNode, RunSheetTemplateEntities } from "./run-sheet.types";
import { TimeRulesCellEditor } from "./time-rules-cell-editor";
import { TimeRulesCellEditorParams } from "./time-rules.types";
import { getDisplayTime, getZeroRowId, hasATimeRule, isRelativeToEndTime, isRelativeToStartTime, isRelativeToZeroTime, isZeroRow } from "./run-sheet-helpers";
import { EventDataFieldInputs } from "./event-data-field-inputs";
import { getDisciplineDataField } from "./datafields";
import { findInheritedDataFieldValue } from "./schedule-helper-functions";
import { getEventBlockHierarchyWithEventBlockId } from "./schedule-api-functions";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { DurationCellEditor, DurationCellEditorRef } from "./schedule-table.components";
import { getEventBlockDisplayValuesFromDataFieldValues, subSessionDefaultColour } from "./event-block-helpers";

interface ScheduledItemActivityCellEditorParams {
    dataFields: DataField[];
    scheduleVersionHierarchy: Hierarchy<ScheduleVersion>;
}

type ScheduledItemActivityCellEditorValues = Record<string, DataFieldListItem | DataFieldListItem[]>;

const ScheduledItemActivityCellEditor = forwardRef<TimeCellEditorRef, ScheduledItemActivityCellEditorParams & IventisCellEditorParams<RunSheetData>>((props, ref) => {
    const { node, eGridCell, dataFields, scheduleVersionHierarchy, value } = props;
    const eventBlockTypeDataFields = useMemo(() => node.data.type?.dataFields.sort((a, b) => a.index - b.index), [node.data.type?.dataFields]);
    // event data field values
    const initialDataFieldValues = useMemo(
        () => eventBlockTypeDataFields.reduce((cum, df) => ({ ...cum, [df.dataFieldId]: node.data[df.dataFieldId] }), {} as ScheduledItemActivityCellEditorValues),
        [node.data]
    );
    const [dataFieldValues, setDataFieldValues] = useState(initialDataFieldValues);
    const setFieldValue = (_, value: Record<string, DataFieldListItem | DataFieldListItem[]>) => setDataFieldValues(value);
    const [disciplineCodeDataFieldValue, setDisciplineCodeDataFieldValue] = useState<DataFieldValue>(null);

    useEffect(() => {
        if (node.data.sessionId) {
            getEventBlockHierarchyWithEventBlockId(node.data.sessionId).then((eventBlockHierarchy) => {
                const scheduledItem = findHierarchyEntityById(eventBlockHierarchy.children, node.data.id);
                const scheduleDisciplineCodeValue = findInheritedDataFieldValue(
                    scheduledItem || eventBlockHierarchy,
                    getDisciplineDataField(dataFields)?.id,
                    eventBlockHierarchy,
                    scheduleVersionHierarchy
                );
                if (scheduleDisciplineCodeValue == null) {
                    throw new Error("Discipline schedule does not have a discipline code");
                }
                setDisciplineCodeDataFieldValue({ dataField: getDisciplineDataField(dataFields), value: scheduleDisciplineCodeValue });
            });
        }
    }, []);

    useImperativeHandle(ref, () => ({
        getValue() {
            Object.entries(dataFieldValues).forEach(([key, value]) => {
                props.node.setDataValue(key, value);
            });
            const values = Object.entries(dataFieldValues).reduce((cum, [key, value]) => {
                const dataField = dataFields.find((df) => df.id === key);
                if (value != null) {
                    cum.push({ dataField, value });
                }
                return cum;
            }, []);
            return getEventBlockDisplayValuesFromDataFieldValues(values, node.data.type?.dataFields).displayText ?? value;
        },
        isCancelBeforeStart() {
            return !node.data.canEdit;
        },
        isCancelAfterEnd() {
            return Object.entries(dataFieldValues).some(
                ([dataFieldId, value]) => node.data.type?.dataFields.find((dfs) => dfs.dataFieldId === dataFieldId)?.required && value == null
            );
        },
        isPopup() {
            return true;
        },
        getPopupPosition() {
            return "over";
        },
    }));

    return (
        <Popover
            open
            anchorEl={eGridCell}
            onClose={() => props.api.stopEditing()}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
        >
            <StyledEventDataFieldInputsContainer>
                <EventDataFieldInputs
                    eventBlockTypeDataFields={eventBlockTypeDataFields}
                    allDataFields={dataFields}
                    additionalNonEventBlockDataFieldValues={disciplineCodeDataFieldValue == null ? [] : [disciplineCodeDataFieldValue]}
                    setFieldValue={setFieldValue}
                    values={{ dataFieldValues }}
                />
            </StyledEventDataFieldInputsContainer>
        </Popover>
    );
});

const StyledEventDataFieldInputsContainer = styled.div`
    ${customMediaQueryMax(screenSizeBreakpoints.medium)} {
        width: 376px;
    }
    ${customMediaQueryMin(screenSizeBreakpoints.medium)} {
        width: 624px;
    }
    background-color: ${subSessionDefaultColour.fill};
    border: 2px solid ${subSessionDefaultColour.outline};
    padding: ${formPadding};
    display: flex;
    flex-direction: column;
    gap: ${formFieldVerticalGap};
    ${muiInputFormsCSS}

    .MuiInputBase-root {
        background-color: white;
    }
    .MuiOutlinedInput-notchedOutline {
        border-color: rgba(0, 0, 0, 0.7);
        border-width: 1.5px;
    }
`;

export const ActivityCellEditor = forwardRef<TimeCellEditorRef, ScheduledItemActivityCellEditorParams & IventisCellEditorParams<RunSheetData>>((props, ref) => {
    const { node } = props;
    const { data } = node;
    const calendarLevel = data.type?.calendarLevel;

    const textCellEditorRef = useRef<TimeCellEditorRef>();
    const scheduledItemActivityCellEditorRef = useRef<TimeCellEditorRef>();

    useImperativeHandle(ref, () => {
        switch (calendarLevel) {
            case 1: // If session
                return {
                    getValue() {
                        throw new Error(`Cannot edit activity for ${calendarLevel}`);
                    },
                    isCancelBeforeStart() {
                        return true;
                    },
                    isCancelAfterEnd() {
                        return true;
                    },
                };
            case 2: // If event unit
                return {
                    getValue() {
                        return scheduledItemActivityCellEditorRef.current?.getValue();
                    },
                    isCancelBeforeStart() {
                        return scheduledItemActivityCellEditorRef.current?.isCancelBeforeStart();
                    },
                    isCancelAfterEnd() {
                        return scheduledItemActivityCellEditorRef.current?.isCancelAfterEnd();
                    },

                    isPopup() {
                        return scheduledItemActivityCellEditorRef.current?.isPopup();
                    },

                    getPopupPosition() {
                        return scheduledItemActivityCellEditorRef.current?.getPopupPosition();
                    },
                };
            default:
                // If activity/other

                if (isZeroRow(node.data)) {
                    return {
                        getValue() {
                            throw new Error(`Cannot edit activity for zero time`);
                        },
                        isCancelBeforeStart() {
                            return true;
                        },
                        isCancelAfterEnd() {
                            return true;
                        },
                    };
                }
                return {
                    getValue() {
                        return textCellEditorRef.current?.getValue();
                    },
                    isCancelBeforeStart() {
                        return textCellEditorRef.current?.isCancelBeforeStart();
                    },
                    isCancelAfterEnd() {
                        return textCellEditorRef.current?.isCancelAfterEnd();
                    },
                };
        }
    });

    switch (calendarLevel) {
        case 1: // If session
            return null;
        case 2: // If event unit
            return <ScheduledItemActivityCellEditor ref={scheduledItemActivityCellEditorRef} {...props} />;
        default:
            // If activity/other
            if (isZeroRow(node.data)) {
                return null;
            }
            return <TextCellEditor ref={textCellEditorRef} {...props} />;
    }
});

export const ActivityCellRenderer: FunctionComponent<IventisCellRendererParams> = ({ value, node, required }) => (
    <TextCellContents dataCy={`activity-${node.data.networkStatus}`} style={{ display: "flex", gap: "10px" }} valid={isValueValid(node, value, required)}>
        {isZeroRow(node.data) && <FontAwesomeIcon icon={["fal", "clock"]} />}
        {value}
    </TextCellContents>
);

/** Go down the relative tree and perform updates based on the given change and field (colId) */
const updateEachNodeInRelationshipTree = (api: IventisGridApi, change: number, parentNodeData: RunSheetData, colId: "startAt" | "endAt", timezone: string) => {
    api.forEachNode((node: RunSheetRowNode) => {
        if (node.data.linkedEventId === parentNodeData.id) {
            if (
                // Should update if related to start time of node which updated its' startAt column
                (isRelativeToStartTime(node.data) && colId === "startAt") ||
                // If the child node is relative to the end time && the parent's end time has been changed then we must update the current node
                (isRelativeToEndTime(node.data) && (hasATimeRule(parentNodeData) || colId === "endAt")) ||
                // If the child node is relative to the zero time && the parents start time has been changed. (Zero time is relative to start time, therefore whenever the start time is updated, the zero time changes too)
                (isRelativeToZeroTime(node.data) && colId === "startAt") ||
                // Parent has a time rule and the column id is null.
                (hasATimeRule(parentNodeData) && colId == null)
            ) {
                // Using the change in seconds, calculate the current nodes' new start and end times
                const newStartAtValue = addSeconds(node.data.startAt.value, change);
                const startDisplayText = getDisplayTime(newStartAtValue, timezone);
                node.setDataValue("startAt", { displayText: startDisplayText, value: newStartAtValue });
                const { endAt } = node.data;
                const newEndAtValue = addSeconds(endAt?.value, change);
                const endDisplayText = getDisplayTime(newEndAtValue, timezone);
                node.setDataValue("endAt", { displayText: endDisplayText, value: newEndAtValue });

                // Update any nodes that might be affected by this change
                updateEachNodeInRelationshipTree(
                    api,
                    change,
                    node.data,
                    null /* After the first level in the recursion, the column id of the initial change is redundant */,
                    timezone
                );
            }
        }
    });
};

/**
 * Used as cellEditor for run sheet startAt column
 */
export const RunSheetStartAtEditor = forwardRef<TimeCellEditorRef, TimeRulesCellEditorParams<RunSheetData>>((props, ref) => {
    const { data } = props.node;
    const { type, id, startAt } = data;

    const isAllowedTimeRule = type.allowedRelativeTypes?.length > 0;

    const timeCellEditorRef = useRef<TimeCellEditorRef>();
    const timeRulesCellEditorRef = useRef<TimeCellEditorRef>();

    useImperativeHandle(ref, () => ({
        getValue() {
            // Get the new start time from the relevant cell editor
            let newValue;
            if (isAllowedTimeRule) {
                newValue = timeRulesCellEditorRef.current?.getValue();
            } else {
                newValue = timeCellEditorRef.current?.getValue();
            }
            // Calculate the change in seconds
            const change = differenceInSeconds(newValue.value, startAt.value);
            // Update any nodes that might be affected by this change
            updateEachNodeInRelationshipTree(props.api, change, data, "startAt", props.context.timezone);
            // Update its zero node
            props.api.forEachNode((zeroNode) => {
                if (zeroNode.data.id === getZeroRowId(id)) {
                    const zeroAtTime = data.zeroAt != null ? addTimeSpanToDate(data.zeroAt as TimeSpan, newValue.value) : newValue.value;
                    const sessionZeroAtDisplayText = format(utcToZonedTime(zeroAtTime, props.context.timezone), "HH:mm:ss");
                    zeroNode.setDataValue("startAt", { value: zeroAtTime, displayText: sessionZeroAtDisplayText });
                }
            });
            // Return the new start at value
            return newValue;
        },
        isCancelBeforeStart() {
            return isZeroRow(data) ? true : isAllowedTimeRule ? timeRulesCellEditorRef.current?.isCancelBeforeStart() : timeCellEditorRef.current?.isCancelBeforeStart();
        },
        isCancelAfterEnd() {
            return isAllowedTimeRule ? timeRulesCellEditorRef.current?.isCancelAfterEnd() : timeCellEditorRef.current?.isCancelAfterEnd();
        },
        isPopup() {
            return isAllowedTimeRule ? timeRulesCellEditorRef.current?.isPopup() : timeCellEditorRef.current?.isPopup();
        },

        // Time rules handles only
        getPopupPosition() {
            return isAllowedTimeRule ? timeRulesCellEditorRef.current?.getPopupPosition() : undefined;
        },
    }));

    return <>{isAllowedTimeRule ? <TimeRulesCellEditor ref={timeRulesCellEditorRef} {...props} /> : <TimeCellEditor<RunSheetData> ref={timeCellEditorRef} {...props} />}</>;
});

/**
 * Used as cellEditor for run sheet endAt column
 */
export const RunSheetEndAtEditor = forwardRef<TimeCellEditorRef, TimeRulesCellEditorParams<RunSheetData>>((props, ref) => {
    const { data } = props.node;
    const { id, endAt, startAt } = data;

    const hasTimeRules = hasATimeRule(data);

    const timeCellEditorRef = useRef<TimeCellEditorRef>();

    useImperativeHandle(ref, () => ({
        getValue() {
            // Get the new end at value
            const newValue = timeCellEditorRef.current?.getValue();
            if (hasTimeRules) {
                const duration = intervalToDuration({ start: startAt.value, end: newValue.value });
                // Update all the rules first (when ag-grid updates these one after the other, we can block the identical requests, based on the last edit)
                props.api.forEachNode(
                    (node) =>
                        node.data.id === id &&
                        node.setDataValue("relativeEventRules", [
                            { ...node.data.relativeEventRules[0], duration: formatTimeSpan(Preposition.After, duration.hours, duration.minutes, duration.seconds) },
                        ])
                );
                // Then update the rest
                props.api.forEachNode((node: RunSheetRowNode) => {
                    // Find all instances of this entity and update accordingly
                    if (node.data.id === id) {
                        const date = add(node.data.startAt.value, duration);
                        const timeCellValue: TimeCellValue = { displayText: getDisplayTime(date, props.context.timezone), value: date };
                        node.setDataValue("endAt", timeCellValue);
                    }
                });
            }
            // Calculate the change in seconds
            const change = differenceInSeconds(newValue.value, endAt.value);
            // Update any nodes that might be affected by this change
            updateEachNodeInRelationshipTree(props.api, change, data, "endAt", props.context.timezone);
            // Return the new end at value
            return newValue;
        },
        isCancelBeforeStart() {
            return timeCellEditorRef.current?.isCancelBeforeStart();
        },
        isCancelAfterEnd() {
            return timeCellEditorRef.current?.isCancelAfterEnd();
        },
        isPopup() {
            return timeCellEditorRef.current?.isPopup();
        },
    }));

    return <TimeCellEditor ref={timeCellEditorRef} {...props} />;
});

/**
 * Used as cellEditor for run sheet location column
 */
export const LocationCellEditor = forwardRef<TimeCellEditorRef, ListItemCellEditorProps<RunSheetData>>((props, ref) => {
    const { node } = props;
    const { data } = node;

    const listItemCellEditorRef = useRef<TimeCellEditorRef>();

    useImperativeHandle(ref, () => ({
        getValue() {
            const value = listItemCellEditorRef.current?.getValue();
            props.api.forEachNode((instanceOfNode) => {
                if (instanceOfNode.data.id === node.data.id && node.id !== instanceOfNode.id) {
                    instanceOfNode.setDataValue("venueLocation", value);
                }
            });
            return value;
        },
        isCancelBeforeStart() {
            // Block the editing of a session or session-zero row's venue
            if (data.type.calendarLevel === 1 || isZeroRow(data)) {
                return true;
            }
            return listItemCellEditorRef.current?.isCancelBeforeStart();
        },
        isCancelAfterEnd() {
            return listItemCellEditorRef.current?.isCancelAfterEnd();
        },
    }));

    return <ListItemCellEditorComponent ref={listItemCellEditorRef} {...props} />;
});

/**
 * Used as cellRenderer for run sheet location column
 */
export const LocationCellRenderer: FunctionComponent<ListItemCellComponentProps<RunSheetData>> = (props) => {
    const { node } = props;
    const { data } = node;

    // We don't display the session-zero row's venue
    return data.type.name === RunSheetTemplateEntities.Zero ? <></> : <CellWithDropDown>{data.venueLocation?.name}</CellWithDropDown>;
};

/**
 * Used as FilterFramework for run sheet location column
 */
export const LocationFilter = forwardRef<IventisFilterRef, ListItemFilterComponentProps>((props, ref) => {
    const listItemFilterRef = useRef<IventisFilterRef>();

    useImperativeHandle(ref, () => ({
        isFilterActive() {
            return listItemFilterRef.current?.isFilterActive();
        },
        getModel() {
            return listItemFilterRef.current?.getModel();
        },
        doesFilterPass(params) {
            // Always show sessions and it's zero row
            if (params.data.type.calendarLevel === 1 || isZeroRow(params.data)) {
                return true;
            }
            return listItemFilterRef.current?.doesFilterPass(params);
        },
    }));

    return <ListItemFilterComponent ref={listItemFilterRef} {...props} />;
});

export const RunSheetDurationCellEditor = forwardRef<IventisCellEditor, IventisCellEditorParams<RunSheetData>>((props, ref) => {
    const durationRef = useRef<DurationCellEditorRef>();
    useImperativeHandle(ref, () => ({
        getValue() {
            // Get the duration in minutes
            const minutes = durationRef.current?.getValue(true); // Passing true into getValue means we just get the value and perform the cell edit ourselves
            // Convert into time span format
            const duration = minutesToTimeSpan(minutes);
            // Get the new end time as a result
            const newEndTime = addMinutes(props.node.data.startAt.value, minutes);
            // The resulting difference in seconds
            const change = differenceInSeconds(newEndTime, props.node.data.endAt.value);

            props.api.forEachNode((instanceOfNode: RunSheetRowNode) => {
                // Find all instances of this entity and update accordingly
                if (instanceOfNode.data.id === props.node.data.id) {
                    const date = addMinutes(instanceOfNode.data.startAt.value, minutes);
                    const timeCellValue: TimeCellValue = { displayText: getDisplayTime(date, props.context.timezone), value: date };
                    instanceOfNode.setDataValue("endAt", timeCellValue);
                }
            });

            // Update any nodes that might be affected by this change
            updateEachNodeInRelationshipTree(props.api, change, props.node.data, "endAt", props.context.timezone);

            // If it has a time rule, update the relative rule with the new duration (for each instance)
            if (hasATimeRule(props.node.data)) {
                const newRelativeRules = [{ ...props.node.data.relativeEventRules[0], duration } as RelativeEventRule];
                props.api.forEachNode((instanceOfNode) => instanceOfNode.data.id === props.node.data.id && instanceOfNode.setDataValue("relativeEventRules", newRelativeRules));
            }
            return duration;
        },
        isCancelAfterEnd() {
            if (hasATimeRule(props.node.data)) {
                const minutes = durationRef.current?.getValue(true);
                const duration = minutesToTimeSpan(minutes);
                if (props.node.data.relativeEventRules[0]?.duration === duration) {
                    return true;
                }
            }
            return durationRef.current?.isCancelAfterEnd();
        },
        isCancelBeforeStart() {
            return durationRef.current?.isCancelBeforeStart();
        },
    }));

    return <DurationCellEditor ref={durationRef} {...(props as IventisCellEditorParams<IventisTimeCellData>)} />;
});
